import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Country, State } from 'country-state-city';
import { getCountryCallingCode } from 'libphonenumber-js';
import { stripePromise } from '../../utils/stripe';
import { useNetwork } from '../../utils/network';
import { track } from '../../utils/track';
import {
  PT,
  fieldSx,
  primaryBtnSx,
  ghostBtnSx,
  panelSx,
  kickerSx
} from '../../components/portalUi';
import { selectMenuProps, selectSx } from '../../components/portalSelect';
import SiteHeader from '../Home/DynamicHomepage/components/Landing/SiteHeader';
import AuctionHomeSection from '../Auction/AuctionHomeSection';
import {
  GOLD,
  GOLD_SOFT,
  GOLD_BORDER,
  aud,
  OPTIONS,
  PACKS_PER,
  MAX_QTY,
  Tilt,
  PackFan,
  PaymentForm
} from '../MemberPortal/PhysicalCards/PhysicalCards';
import type { OptionId } from '../MemberPortal/PhysicalCards/PhysicalCards';
import angelicPack from '../MemberPortal/PhysicalCards/assets/angelic-pack.png';

// The public T&C document — the same one the sign-up page links.
const TERMS_URL =
  'https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/documents/terms_and_conditions_05_08_2026.docx';

// Marketing: show the scarcity badge now, ahead of real stock hitting the threshold
// (mirrors the member storefront's FORCE_ALMOST_SOLD_OUT).
const FORCE_ALMOST_SOLD_OUT = true;

type Step = 'choose' | 'shipping' | 'pay' | 'done';

// The public buyer's details — the shipping form doubles as a SIGN-UP form, so it
// carries first/last name (the new account's name) as well as the address.
type Form = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string; // local part; the country calling code is prefixed on submit
  line1: string;
  line2: string;
  city: string;
  state: string;
  postcode: string;
  country: string; // ISO alpha-2
};

const EMPTY: Form = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postcode: '',
  country: 'AU'
};

const callingCodeFor = (iso: string) => {
  try {
    return iso ? String(getCountryCallingCode(iso as any)) : '';
  } catch {
    return '';
  }
};

/* The PUBLIC homepage physical-card storefront. Same packs + Stripe payment as the
   member store, but open to visitors: the shipping form is also a sign-up form. The
   buyer agrees to the T&Cs and to an account being created, pays, and only THEN does
   the server (in the webhook) create their passwordless INCRYPT account and email
   them to set a password. Nothing about a user is created before payment. */
export const PublicPhysicalCards: React.FC = () => {
  const { get, post } = useNetwork();
  const [step, setStep] = useState<Step>('choose');
  const [selectedId, setSelectedId] = useState<OptionId | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [form, setForm] = useState<Form>(EMPTY);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeAccount, setAgreeAccount] = useState(false);
  // "Pack my order on video for INCRYPT's socials" — an opt-in kept on the order
  const [packOnVideo, setPackOnVideo] = useState(false);
  const [payment, setPayment] = useState<{
    clientSecret: string;
    amount: number;
    publishableKey?: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [stock, setStock] = useState<{
    cap: number;
    remaining: number;
    soldOut: boolean;
  } | null>(null);
  const [shippingQuote, setShippingQuote] = useState<{
    cost: number;
    service: string | null;
  } | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(
    () => State.getStatesOfCountry(form.country) || [],
    [form.country]
  );
  const callingCode = useMemo(() => callingCodeFor(form.country), [form.country]);

  const selected = useMemo(
    () => OPTIONS.find((o) => o.id === selectedId) || null,
    [selectedId]
  );

  const refreshStock = async () => {
    try {
      const res = await get('/checkout/physical-card-stock-public');
      if (res && typeof res.remaining === 'number') {
        setStock({
          cap: Number(res.cap) || 950,
          remaining: Number(res.remaining),
          soldOut: !!res.soldOut
        });
      }
    } catch {
      /* non-fatal — the page still works without the stock badge */
    }
  };

  useEffect(() => {
    refreshStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeStripe = useMemo(
    () =>
      payment?.publishableKey ? loadStripe(payment.publishableKey) : stripePromise,
    [payment?.publishableKey]
  );

  const soldOutFor = (id: OptionId) =>
    !!stock && PACKS_PER[id] > 0 && stock.remaining < PACKS_PER[id];
  const almostSoldOut = FORCE_ALMOST_SOLD_OUT || (!!stock && stock.remaining > 0);

  const maxQtyFor = (id: OptionId): number => {
    const per = PACKS_PER[id];
    if (per <= 0) return MAX_QTY;
    const byStock = stock ? Math.floor(stock.remaining / per) : MAX_QTY;
    return Math.max(1, Math.min(MAX_QTY, byStock));
  };
  const maxQty = selected ? maxQtyFor(selected.id) : MAX_QTY;

  // Live shipping quote for the summary (re-quoted authoritatively at payment time).
  useEffect(() => {
    if (!selected) return;
    const country = form.country.trim().toUpperCase();
    const postcode = form.postcode.trim();
    if (!country || (country === 'AU' && !postcode)) {
      setShippingQuote(null);
      setQuoteLoading(false); // clear a spinner left over from a prior valid entry
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await post('/checkout/physical-card-shipping-quote-public', {
          option: selected.id,
          quantity,
          shipping: { country, postcode }
        });
        if (!cancelled && res && typeof res.shipping === 'number') {
          setShippingQuote({ cost: res.shipping, service: res.service || null });
        }
      } catch {
        if (!cancelled) setShippingQuote(null);
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, quantity, form.country, form.postcode]);

  useEffect(() => {
    setQuantity((q) => Math.min(Math.max(1, q), maxQty));
  }, [maxQty]);

  const set =
    (k: keyof Form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((s) => ({ ...s, [k]: e.target.value }));

  const choose = (id: OptionId) => {
    setSelectedId(id);
    setQuantity(1);
    setFormError(null);
    setStep('shipping');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitShipping = async () => {
    if (!selected) return;
    const required: Array<keyof Form> = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'line1',
      'city',
      'postcode',
      'country'
    ];
    if (states.length > 0) required.push('state');
    if (required.some((k) => !form[k].trim())) {
      setFormError('Please fill in every required field.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      setFormError('Please enter a valid email address.');
      return;
    }
    if (!agreeTerms || !agreeAccount) {
      setFormError('Please tick both boxes to continue.');
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const attemptId =
        (window.crypto &&
          window.crypto.randomUUID &&
          window.crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const countryName =
        countries.find((c) => c.isoCode === form.country)?.name || form.country;
      const res = await post('/checkout/physical-card-payment-intent-public', {
        option: selected.id,
        quantity,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        countryName,
        shipping: {
          email: form.email.trim(),
          phone: `+${callingCode}${form.phone.replace(/\D/g, '')}`,
          line1: form.line1.trim(),
          line2: form.line2.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          postcode: form.postcode.trim(),
          country: form.country.trim().toUpperCase()
        },
        attemptId,
        agreeTerms,
        agreeAccount,
        packOnVideo
      });
      if (!res?.clientSecret) throw new Error('No client secret returned.');
      setPayment({
        clientSecret: res.clientSecret,
        amount: Number(res.amount) || selected.price,
        publishableKey: res.publishableKey
      });
      track('physical_card.checkout_started', {
        option: selected.id,
        surface: 'public'
      });
      setStep('pay');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      setFormError(e?.message || 'Could not start the payment. Please try again.');
      refreshStock();
    }
    setSubmitting(false);
  };

  return (
    <Box sx={{ background: '#070c13', minHeight: '100vh' }}>
      <SiteHeader />
      <Box
        sx={{
          maxWidth: 1120,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 5 },
          color: PT.text,
          fontFamily: 'Montserrat, sans-serif'
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: 28, md: 40 },
              lineHeight: 1.1,
              background: `linear-gradient(180deg, #fff, ${GOLD})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Incrypt Physical Cards
          </Typography>
          <Typography
            sx={{
              mt: 1.25,
              color: GOLD,
              fontWeight: 700,
              fontSize: { xs: 15, md: 17 }
            }}
          >
            Pre-purchase the limited first run of Angelic Faction packs
          </Typography>
        </Box>

        {/* The physical card-set auction, when one is on (renders nothing otherwise):
            the lot and the leading bid, with the sign-in door — bidding is a member's. */}
        {step === 'choose' && <AuctionHomeSection tight />}

        {/* Scarcity banner — one line across the top of the drop rather than a
            number on each card. On the cards it read as a per-product label; here
            it reads as what it is, a countdown on the whole run.

            Hidden once sold out: the cards say so themselves, and "0 packs left"
            is shouting at a closed door. Hidden too until stock has loaded, so the
            page never flashes a figure it is about to correct. */}
        {step === 'choose' && stock && !stock.soldOut && stock.remaining > 0 && (
          <Box
            sx={{
              position: 'relative',
              isolation: 'isolate',
              mb: 4,
              pt: { xs: 3, md: 4 },
              pb: { xs: 3, md: 4 },
              px: 2,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                zIndex: -1,
                pointerEvents: 'none',
                background:
                  'radial-gradient(60% 90% at 50% 55%, rgba(242,162,75,0.22), rgba(242,162,75,0.06) 55%, transparent 100%)'
              }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                mb: 1.5,
                '&::before, &::after': {
                  content: '""',
                  height: '1px',
                  width: { xs: 34, md: 72 },
                  background: 'linear-gradient(90deg, transparent, rgba(242,162,75,0.75))'
                },
                '&::after': {
                  background: 'linear-gradient(90deg, rgba(242,162,75,0.75), transparent)'
                }
              }}
            >
              <Typography
                sx={{
                  font: '800 11px ui-monospace, Menlo, Consolas, monospace',
                  letterSpacing: { xs: '0.2em', sm: '0.32em' },
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                  color: '#f2a24b'
                }}
              >
                First run · almost sold out
              </Typography>
            </Box>
            <Typography
              component="h2"
              sx={{
                textAlign: 'center',
                mt: 0,
                mb: 2.5,
                mx: 'auto',
                px: 2,
                maxWidth: 760,
                color: '#fff',
                fontWeight: 800,
                fontSize: { xs: 24, sm: 32, md: 40 },
                lineHeight: 1.15,
                letterSpacing: '0.01em',
                textWrap: 'balance',
                textShadow: '0 4px 40px rgba(242,162,75,0.35)'
              }}
            >
              Don&rsquo;t miss out on your chance to get the{' '}
              <Box
                component="span"
                sx={{
                  color: '#f2a24b',
                  whiteSpace: { xs: 'normal', md: 'nowrap' },
                  backgroundImage: 'linear-gradient(90deg, #ffd27a, #f2a24b)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                last few remaining packs
              </Box>
              .
            </Typography>
            <Box
              sx={{
                mx: 'auto',
                mb: 0,
                maxWidth: 720,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.25,
                flexWrap: 'wrap',
                textAlign: 'center',
                px: { xs: 2, md: 3 },
                py: { xs: 1.4, md: 1.75 },
                borderRadius: 2,
                background:
                  'linear-gradient(180deg, rgba(242,162,75,0.20), rgba(242,162,75,0.07))',
                border: '1px solid rgba(242,162,75,0.55)',
                boxShadow: '0 0 26px rgba(242,162,75,0.18)'
              }}
            >
              <WhatshotIcon sx={{ fontSize: 24, color: '#f2a24b' }} />
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: 18, md: 22 },
                  color: '#f2a24b',
                  letterSpacing: '0.01em'
                }}
              >
                Only {stock.remaining} pack{stock.remaining === 1 ? '' : 's'} left
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 13, md: 14 },
                  color: 'rgba(255,255,255,0.72)'
                }}
              >
                Once they&rsquo;re gone, they&rsquo;re gone.
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Step: choose ─────────────────────────────────────────────────── */}
        {step === 'choose' && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2.5,
              alignItems: 'stretch'
            }}
          >
            {OPTIONS.map((o) => (
              <Box
                key={o.id}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '18px',
                  overflow: 'visible',
                  border: `1px solid ${o.featured ? GOLD_BORDER : PT.border}`,
                  background: o.featured
                    ? `radial-gradient(120% 80% at 50% -10%, ${GOLD_SOFT}, transparent 55%), ${PT.surface}`
                    : PT.surface,
                  boxShadow: o.featured
                    ? '0 20px 50px -20px rgba(232,200,119,0.35)'
                    : 'none',
                  transition: 'transform .18s ease, border-color .18s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: GOLD_BORDER,
                    zIndex: 4
                  }
                }}
              >
                <Box
                  sx={{
                    height: 260,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '18px 18px 0 0',
                    background:
                      'radial-gradient(80% 80% at 50% 30%, rgba(232,200,119,0.10), transparent 70%)'
                  }}
                >
                  <Tilt
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'grid',
                      placeItems: 'center'
                    }}
                  >
                    {/* The holder is not a pack — showing pack art on a card
                        that says "No packs included" is the kind of small lie a
                        buyer notices. Mirrors the member storefront. */}
                    {o.boxOnly ? (
                      <Box sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.55)' }}>
                        <Inventory2Icon
                          sx={{ fontSize: 74, color: GOLD, opacity: 0.85 }}
                        />
                        <Typography
                          sx={{ mt: 1, fontSize: 11, letterSpacing: '0.1em' }}
                        >
                          BOX ART COMING SOON
                        </Typography>
                      </Box>
                    ) : o.fan ? (
                      <PackFan count={o.fan} />
                    ) : (
                      <Box
                        component="img"
                        src={angelicPack}
                        alt="Angelic Faction pack"
                        sx={{
                          height: 240,
                          maxWidth: '80%',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.5))'
                        }}
                      />
                    )}
                  </Tilt>
                </Box>
                <Box
                  sx={{ p: 2.5, display: 'flex', flexDirection: 'column', flex: 1 }}
                >
                  {/* Scarcity is a PACK fact — the holder does not draw from
                      the pack cap, so it never runs out with them. Mirrors the
                      member storefront. */}
                  {almostSoldOut && PACKS_PER[o.id] > 0 && !soldOutFor(o.id) && (
                    <Box
                      sx={{
                        alignSelf: 'center',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        mb: 1.25,
                        px: 1.5,
                        py: 0.6,
                        borderRadius: '999px',
                        background: 'rgba(242,162,75,0.15)',
                        border: '1px solid rgba(242,162,75,0.55)',
                        color: '#f2a24b',
                        fontWeight: 800,
                        fontSize: 12.5,
                        textTransform: 'uppercase'
                      }}
                    >
                      <WhatshotIcon sx={{ fontSize: 16 }} /> Almost sold out
                    </Box>
                  )}
                  <Typography sx={{ fontWeight: 800, fontSize: 20 }}>
                    {o.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: GOLD,
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}
                  >
                    {o.tagline}
                  </Typography>
                  <Typography sx={{ mt: 1.5, fontWeight: 900, fontSize: 30 }}>
                    {aud(o.price)}
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ listStyle: 'none', p: 0, m: '14px 0 0', flex: 1 }}
                  >
                    {o.includes.map((line) => (
                      <Box
                        component="li"
                        key={line}
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-start',
                          mb: 0.75,
                          color: PT.textMuted,
                          fontSize: 13.5
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            fontSize: 16,
                            color: GOLD,
                            mt: '2px',
                            flexShrink: 0
                          }}
                        />
                        {line}
                      </Box>
                    ))}
                  </Box>
                  <Button
                    disabled={soldOutFor(o.id)}
                    onClick={() => choose(o.id)}
                    sx={{
                      mt: 2,
                      py: 1.05,
                      fontWeight: 800,
                      textTransform: 'none',
                      borderRadius: '10px',
                      ...(o.featured
                        ? {
                            background: `linear-gradient(180deg, ${GOLD}, #cfa947)`,
                            color: '#231a00',
                            '&:hover': {
                              background: `linear-gradient(180deg, #f2d98a, ${GOLD})`
                            }
                          }
                        : ghostBtnSx)
                    }}
                  >
                    {soldOutFor(o.id) ? 'Sold out' : 'Pre-purchase'}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {step === 'choose' && (
          <Typography
            sx={{
              mt: { xs: 3, md: 4 },
              color: PT.textMuted,
              maxWidth: 720,
              mx: 'auto',
              textAlign: 'center',
              fontSize: 15,
              lineHeight: 1.6
            }}
          >
            Reserve the first run of real, physical INCRYPT trading cards — a{' '}
            <b style={{ color: PT.text }}>limited, custom-made</b> series printed
            only for this drop. Every pack holds{' '}
            <b style={{ color: PT.text }}>10 cards</b> of the Angelic Faction. This
            is a pre-purchase; orders ship once the first print run is complete. An
            INCRYPT account is created for you at checkout so you can track your order
            and play online.
          </Typography>
        )}

        {/* ── Step: shipping / pay ─────────────────────────────────────────── */}
        {(step === 'shipping' || step === 'pay') && selected && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 340px' },
              gap: 3,
              alignItems: 'start'
            }}
          >
            <Box sx={{ ...panelSx, p: { xs: 2, md: 3 } }}>
              <Button
                onClick={() => setStep(step === 'pay' ? 'shipping' : 'choose')}
                startIcon={<ArrowBackIcon />}
                sx={{ ...ghostBtnSx, mb: 2, px: 1.5, py: 0.5 }}
              >
                {step === 'pay' ? 'Details' : 'All options'}
              </Button>

              {step === 'shipping' ? (
                <>
                  <Typography
                    sx={{
                      ...kickerSx,
                      color: GOLD,
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center'
                    }}
                  >
                    <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} /> Your details
                    &amp; shipping
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1.75,
                      '& .MuiInputBase-input': { py: '14px' }
                    }}
                  >
                    <TextField
                      label="First name *"
                      value={form.firstName}
                      onChange={set('firstName')}
                      sx={fieldSx}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Last name *"
                      value={form.lastName}
                      onChange={set('lastName')}
                      sx={fieldSx}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Email *"
                      value={form.email}
                      onChange={set('email')}
                      sx={fieldSx}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Phone *"
                      value={form.phone}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          phone: e.target.value.replace(/[^\d\s]/g, '')
                        }))
                      }
                      sx={fieldSx}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: callingCode ? (
                          <Typography sx={{ mr: 0.75, fontWeight: 800, color: PT.textMuted }}>
                            +{callingCode}
                          </Typography>
                        ) : undefined
                      }}
                    />
                    <TextField
                      label="Address line 1 *"
                      value={form.line1}
                      onChange={set('line1')}
                      sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Address line 2"
                      value={form.line2}
                      onChange={set('line2')}
                      sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="City / Suburb *"
                      value={form.city}
                      onChange={set('city')}
                      sx={fieldSx}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Postcode *"
                      value={form.postcode}
                      onChange={set('postcode')}
                      sx={fieldSx}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      select
                      label="Country *"
                      value={form.country}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          country: e.target.value,
                          state: ''
                        }))
                      }
                      sx={selectSx}
                      SelectProps={{ MenuProps: selectMenuProps }}
                      fullWidth
                      size="small"
                    >
                      {countries.map((c) => (
                        <MenuItem key={c.isoCode} value={c.isoCode}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    {states.length > 0 ? (
                      <TextField
                        select
                        label="State / Province *"
                        value={form.state}
                        onChange={set('state')}
                        sx={selectSx}
                        SelectProps={{ MenuProps: selectMenuProps }}
                        fullWidth
                        size="small"
                      >
                        {states.map((st) => (
                          <MenuItem key={st.isoCode} value={st.name}>
                            {st.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField
                        label="State / Province"
                        value={form.state}
                        onChange={set('state')}
                        sx={fieldSx}
                        fullWidth
                        size="small"
                      />
                    )}
                  </Box>

                  {/* Mandatory consents — the form doubles as a sign-up. */}
                  <FormControlLabel
                    sx={{ mt: 1.5, ml: 0, alignItems: 'flex-start' }}
                    control={
                      <Checkbox
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                        sx={{ color: PT.textMuted, py: 0.5, '&.Mui-checked': { color: GOLD } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13.5, color: PT.textMuted, mt: '6px' }}>
                        I have read and agree to the{' '}
                        <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer" sx={{ color: GOLD }}>
                          terms and conditions
                        </Link>
                        .
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    sx={{ mt: 0.5, ml: 0, alignItems: 'flex-start' }}
                    control={
                      <Checkbox
                        checked={agreeAccount}
                        onChange={(e) => setAgreeAccount(e.target.checked)}
                        sx={{ color: PT.textMuted, py: 0.5, '&.Mui-checked': { color: GOLD } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13.5, color: PT.textMuted, mt: '6px' }}>
                        I understand an INCRYPT account will be created from these
                        details, and I'll be emailed to set a password.
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    sx={{ mt: 0.5, ml: 0, alignItems: 'flex-start' }}
                    control={
                      <Checkbox
                        checked={packOnVideo}
                        onChange={(e) => setPackOnVideo(e.target.checked)}
                        sx={{ color: PT.textMuted, py: 0.5, '&.Mui-checked': { color: GOLD } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13.5, color: PT.textMuted, mt: '6px' }}>
                        Pack my order on video for INCRYPT's socials — I'm happy for the
                        unboxing of my parcel to be filmed and shared. (Optional)
                      </Typography>
                    }
                  />
                </>
              ) : (
                payment && (
                  <>
                    <Typography sx={{ ...kickerSx, color: GOLD, mb: 2 }}>
                      Payment
                    </Typography>
                    <Elements
                      stripe={activeStripe}
                      options={{
                        clientSecret: payment.clientSecret,
                        appearance: {
                          theme: 'night',
                          variables: {
                            colorPrimary: PT.teal,
                            colorBackground: PT.surface,
                            colorText: PT.text,
                            borderRadius: '10px',
                            fontFamily: 'Montserrat, sans-serif'
                          }
                        }
                      }}
                    >
                      <PaymentForm
                        amount={payment.amount}
                        billing={{
                          name: `${form.firstName} ${form.lastName}`.trim(),
                          email: form.email,
                          phone: form.phone
                            ? `+${callingCode}${form.phone.replace(/\D/g, '')}`
                            : undefined,
                          address: {
                            line1: form.line1,
                            line2: form.line2 || undefined,
                            city: form.city,
                            state: form.state || undefined,
                            postal_code: form.postcode,
                            country: form.country
                          }
                        }}
                        onBack={() => setStep('shipping')}
                        onDone={() => setStep('done')}
                      />
                    </Elements>
                  </>
                )
              )}
            </Box>

            {/* Order summary */}
            <Box
              sx={{
                ...panelSx,
                p: 2.5,
                position: { md: 'sticky' },
                top: 16,
                gridColumn: { md: 2 },
                gridRow: { md: '1 / span 2' },
                order: { xs: step === 'pay' ? -1 : 0, md: 0 }
              }}
            >
              <Typography sx={{ ...kickerSx }}>Order summary</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1.75, alignItems: 'center' }}>
                <Box
                  component="img"
                  src={angelicPack}
                  alt=""
                  sx={{
                    width: 56,
                    height: 74,
                    borderRadius: '8px',
                    objectFit: 'contain',
                    border: `1px solid ${PT.border}`,
                    background: 'rgba(255,255,255,0.03)'
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800 }}>{selected.name}</Typography>
                  <Typography sx={{ color: PT.textMuted, fontSize: 12.5 }}>
                    Angelic Faction · {selected.tagline}
                  </Typography>
                </Box>
              </Box>
              {/* Quantity */}
              <Box
                sx={{
                  borderTop: `1px solid ${PT.border}`,
                  mt: 2,
                  pt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography sx={{ color: PT.textMuted }}>Quantity</Typography>
                {step === 'shipping' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      border: `1px solid ${PT.border}`,
                      borderRadius: '10px',
                      px: 0.5
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Decrease quantity"
                      disabled={quantity <= 1}
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      sx={{ color: PT.text, width: 30, height: 30 }}
                    >
                      −
                    </IconButton>
                    <Typography sx={{ minWidth: 26, textAlign: 'center', fontWeight: 800 }}>
                      {quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Increase quantity"
                      disabled={quantity >= maxQty}
                      onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                      sx={{ color: PT.text, width: 30, height: 30 }}
                    >
                      +
                    </IconButton>
                  </Box>
                ) : (
                  <Typography sx={{ fontWeight: 800 }}>×{quantity}</Typography>
                )}
              </Box>
              {/* Shipping */}
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between'
                }}
              >
                <Typography sx={{ color: PT.textMuted, fontSize: 13 }}>
                  Shipping{shippingQuote?.service ? ` · ${shippingQuote.service}` : ''}
                </Typography>
                <Typography sx={{ color: PT.text, fontSize: 13, fontWeight: 700 }}>
                  {quoteLoading
                    ? 'Calculating…'
                    : shippingQuote
                      ? shippingQuote.cost > 0
                        ? aud(shippingQuote.cost)
                        : 'Free'
                      : '—'}
                </Typography>
              </Box>
              {/* Total */}
              <Box
                sx={{
                  borderTop: `1px solid ${PT.border}`,
                  mt: 2,
                  pt: 2,
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between'
                }}
              >
                <Typography sx={{ color: PT.textMuted }}>Total (AUD)</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 20, color: GOLD }}>
                  {aud(
                    step === 'pay' && payment
                      ? payment.amount
                      : selected.price * quantity + (shippingQuote?.cost || 0)
                  )}
                </Typography>
              </Box>
              <Typography
                sx={{ mt: 2, color: PT.textMuted, fontSize: 11.5, lineHeight: 1.5 }}
              >
                Pre-purchase — ships when the first print run is ready. You'll get an
                email receipt at checkout.
              </Typography>
            </Box>

            {step === 'shipping' && (
              <Box sx={{ gridColumn: { md: '1' } }}>
                {formError && (
                  <Typography sx={{ mb: 1.5, color: '#ff8a9b', fontSize: 13 }}>
                    {formError}
                  </Typography>
                )}
                <Button
                  onClick={submitShipping}
                  disabled={submitting || !agreeTerms || !agreeAccount}
                  sx={{ ...primaryBtnSx, py: 1.1, width: '100%' }}
                >
                  {submitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Continue to payment'
                  )}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* ── Step: done ───────────────────────────────────────────────────── */}
        {step === 'done' && selected && (
          <Box
            sx={{
              ...panelSx,
              p: { xs: 3, md: 5 },
              textAlign: 'center',
              maxWidth: 560,
              mx: 'auto'
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 64, color: GOLD }} />
            <Typography sx={{ mt: 1.5, fontWeight: 900, fontSize: 24 }}>
              Pre-purchase confirmed
            </Typography>
            <Typography sx={{ mt: 1.5, color: PT.textMuted }}>
              Thank you for purchasing{' '}
              <b style={{ color: PT.text }}>
                {quantity} × {selected.name}
              </b>
              . We've emailed{' '}
              <b style={{ color: PT.text }}>{form.email}</b> a link to set your
              password and finish setting up your INCRYPT account. Your cards will
              ship once the first print run is ready (4–6 weeks).
            </Typography>
            <Button
              component="a"
              href="/login"
              sx={{ ...primaryBtnSx, mt: 3, px: 3 }}
            >
              Go to login
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PublicPhysicalCards;
